import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sidebar"
export default class extends Controller {
  static targets = [
    'menuButtonArrowIcon',
    'logoFull',
    'logoSimple',
    'menuItem',
    'submenuItem',
    'profileMenuItem',
    'profileName',
    'asideMenu',
    'menu',
    'submenu',
  ]
  static values = { fullLogo: String }

  isCollapsed = true
  activeMenuItem = null

  connect() {
    if (this.isMobile()) {
      this.isCollapsed = true
    } else {
      this.checkFirstLogin()
    }
    this.toggleElements()
    this.asideMenuTarget.classList.remove('hidden!')
  }

  checkFirstLogin() {
    const key = "logged_in_before"
    const hasLoggedInBefore = !!localStorage.getItem(key)

    this.isCollapsed = hasLoggedInBefore

    if (!hasLoggedInBefore) {
      localStorage.setItem(key, true)
    }
  }

  toggle() {
    if (this.isMobile()) return

    this.isCollapsed = !this.isCollapsed
    this.toggleElements()
    setTimeout(() => this.dispatchResize(), 200)
  }

  toggleElements() {
    this.menuTarget.classList.toggle('collapsed', this.isCollapsed)
    this.menuButtonArrowIconTarget.classList.toggle('rotated', !this.isCollapsed)
    this.logoFullTarget.classList.toggle('hidden', this.isCollapsed)
    this.logoSimpleTarget.classList.toggle('hidden', !this.isCollapsed)

    this.menuItemTargets.forEach((item) => {
      item.classList.toggle('collapsed', this.isCollapsed)
      if (item._tippy) {
        this.isCollapsed ? item._tippy.enable() : item._tippy.disable()
      }
    })

    if (this.hasProfileMenuItemTarget) {
      this.profileMenuItemTarget.classList.toggle('justify-center', this.isCollapsed)
    }

    if (this.hasProfileNameTarget) {
      this.profileNameTarget.classList.toggle('hidden', this.isCollapsed)
    }
  }

  mouseenterOnLinkWithSubmenuHandler(event) {
    const menuItem = event.currentTarget


    this.deactivateOtherMenuItems(menuItem)
    this.setSubmenuOpenState(true)
    this.openSubmenu(menuItem)
  }

  toggleMenuItemsArrowIcons() {
    this.menuItemTargets.forEach((item) => {
      const arrowIcon = item.querySelector('.icon-arrow-right')
      if (arrowIcon) {
        arrowIcon.classList.remove('rotated')
      }
    })
  }

  clickOnLinkWithSubmenuHandler(event) {
    const menuItem = event.currentTarget
    this.activeMenuItem = menuItem

    const isActive = menuItem.classList.contains('active')
    const submenu = JSON.parse(menuItem.dataset.submenu)
    const isSubmenuRendered = this.submenuTarget.dataset.renderedSubmenu === menuItem.dataset.submenu
    const firstItemPath = submenu[0]?.path
    const isSubmenuOpen = this.submenuTarget.classList.contains('open')

    this.deactivateOtherMenuItems(menuItem)
    if (!isActive && submenu && firstItemPath) {
      this.redirectToPath(firstItemPath)
      this.setMenuActive(event)
      this.openSubmenu(menuItem)
      this.submenuItemTargets[0]?.classList.add('active')
      this.setSubmenuOpenState(false)
      return
    }

    if (isSubmenuRendered) {
      this.setSubmenuOpenState(!isSubmenuOpen)
    }
  }

  closeSubmenu() {
    this.toggleMenuItemsArrowIcons()
    this.setSubmenuOpenState(false)
  }

  setSubmenuOpenState(flag) {
    this.submenuTarget.classList.toggle('open', flag)
    this.dispatchResize()
  }

  dispatchResize() {
    this.element.dispatchEvent(new CustomEvent('sidebar:resize', { bubbles: true }))
  }

  openSubmenu(menuItem) {
    if (this.submenuTarget.classList.contains('hidden!')) {
      this.submenuTarget.classList.remove('hidden!')
    }

    this.renderSubmenu(menuItem)
  }

  deactivateOtherMenuItems(activeMenuItem) {
    this.menuItemTargets.forEach((item) => {
      if (item !== activeMenuItem) {
        const arrowIcon = item.querySelector('.icon-arrow-right')
        if (arrowIcon) {
          arrowIcon.classList.remove('rotated')
        }
      }
    })
  }

  renderSubmenu(menuItem) {
    this.submenuTarget.dataset.renderedSubmenu = menuItem.dataset.submenu

    const submenuList = JSON.parse(menuItem.dataset.submenu)
    const menuItemIndex = this.menuItemTargets.indexOf(menuItem)

    const submenu = this.submenuTemplate({
      submenu: submenuList,
      title: menuItem.dataset.submenuTitle,
      menuItemIndex
    })
    this.submenuTarget.innerHTML = submenu

    this.submenuItemTargets.forEach((item, index) => {
      const submenuItem = submenuList[index]
      const isActive = submenuItem.path === window.location.pathname
      item.classList.toggle('active', isActive)
    })
  }

  submenuTemplate({ submenu, title, menuItemIndex }) {
    return `
      <div class="aside-submenu-title">${title}</div>
      <ul class="aside-submenu">
        ${submenu.map(item => {
          const turboFrame = item.turbo_frame || "main_content"
          const turboAction = turboFrame === "main_content" ? 'data-turbo-action="advance"' : ""

          return `
            <li class="aside-submenu-item-wrapper">
              <a href="${item.path}"
                class="aside-submenu-item"
                data-turbo-frame="${turboFrame}"
                ${turboAction}
                data-sidebar-target="submenuItem"
                data-action="click->sidebar#setSubmenuActive"
                data-menu-item-index="${menuItemIndex}">
                ${item.title}
              </a>
            </li>
          `
        }).join('')}
      </ul>
    `
  }

  clickOnLinkHandler(event) {
    this.activeMenuItem = event.currentTarget
    this.setMenuActive(event)
  }

  setMenuActive(event) {
    const hasSubmenu = !!event.currentTarget.dataset?.submenu

    if (hasSubmenu) {
      this.submenuItemTargets.forEach(item => item.classList.remove('active'))
    }
    this.setSubmenuActiveClass(event.currentTarget, this.menuItemTargets)
  }

  setSubmenuActive(event) {
    const submenuItem = event.currentTarget
    this.setMenuActiveClass(submenuItem.dataset.menuItemIndex)
    this.setSubmenuActiveClass(submenuItem, this.submenuItemTargets)
    this.setSubmenuOpenState(false)
  }

  setMenuActiveClass(index) {
    this.menuItemTargets.forEach((item, idx) => {
      item.classList.toggle('active', idx === +index)
    })
  }

  setSubmenuActiveClass(target, items) {
    items.forEach(item => {
      item.classList.toggle('active', item === target)
    })
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  redirectToPath(path) {
    const link = document.createElement('a')
    link.href = path
    link.style.display = 'none'
    link.dataset.turboFrame = 'main_content'
    link.dataset.turboAction = 'advance'

    document.body.appendChild(link)

    link.click()

    document.body.removeChild(link)
  }
}
